import { authorizedFetch } from './session';

// The C009 general catalogue/inventory/store system (content/CATALOGUE.md),
// additive alongside the legacy timer-theme cosmetics in `cosmetics.ts` —
// separate tables, separate routes, not a migration of one into the other.
// See docs/2026-08-07_cosmetics_store_handover.md.

export interface CatalogItem {
  id: string;
  slot: string;
  name: string;
  description: string;
  rarity: string;
  fairnessClass: string;
  sources: string[];
  tradeableUp: boolean;
  droppable: boolean;
  seasonId: string | null;
  priceSoft: number | null;
  pricePremiumCents: number | null;
  config: Record<string, unknown>;
  assetRef: string | null;
  active: boolean;
}

export interface Rarity {
  id: string;
  name: string;
  rank: number;
  color: string;
  glow: number;
}

export interface CatalogResponse {
  items: CatalogItem[];
  rarities: Rarity[];
  bundles: { id: string; name: string; description: string; items: string[]; priceSoft: number | null; pricePremiumCents: number | null }[];
  featured: string[];
}

export interface CurrencyBalance {
  id: string;
  name: string;
  balance: number;
}

export interface InventoryResponse {
  owned: string[];
  equipped: Record<string, string>;
  /** Ticks (earnable) and Tocks (premium — purchase or a curated grant only, never gameplay). */
  currencies: CurrencyBalance[];
}

/** Public reference data — every active item, rarity and bundle. No auth required. */
export function fetchCatalog(): Promise<CatalogResponse> {
  return authorizedFetch<CatalogResponse>('/api/catalog');
}

/** The signed-in player's own state: owned items, equipped loadout, currency balance. */
export function fetchInventory(): Promise<InventoryResponse> {
  return authorizedFetch<InventoryResponse>('/api/inventory');
}

/** Equips an owned item into its slot, replacing whatever was there. */
export async function equipItem(itemId: string): Promise<void> {
  await authorizedFetch('/api/inventory/equip', { method: 'POST', body: JSON.stringify({ itemId }) });
}

export interface PlayerTag {
  emblemImage: string | null;
  plateImage: string | null;
  callSignText: string | null;
  bannerImage: string | null;
}

/**
 * A player's "tag" — equipped emblem + call-sign plate/text + profile banner —
 * resolved from catalogue item ids. Extracted from `social-overlay.ts`'s
 * profile header (which used to inline this lookup for "me" only) so a
 * knockout lobby grid can render the same tag for up to 8 different players,
 * keyed off whatever `equipped` map the caller has for each of them.
 *
 * `profile_banner` was equippable via Inventory but never actually resolved
 * anywhere (owner bug report, 2026-08-18: call sign/banner "disappear" on
 * the friends page and elsewhere) — it existed as a real owned/equipped
 * slot with no display surface at all. Added here alongside the emblem/call
 * sign so every caller of this function gets it for free.
 */
export function resolvePlayerTag(catalogById: Map<string, CatalogItem>, equipped: Record<string, string>): PlayerTag {
  const emblemId = equipped['emblem'];
  const callSignId = equipped['call_sign'];
  const bannerId = equipped['profile_banner'];
  const emblem = emblemId ? catalogById.get(emblemId) : undefined;
  const callSign = callSignId ? catalogById.get(callSignId) : undefined;
  const banner = bannerId ? catalogById.get(bannerId) : undefined;
  return {
    emblemImage: emblem && typeof emblem.config.image === 'string' ? emblem.config.image : null,
    plateImage: callSign && typeof callSign.config.image === 'string' ? callSign.config.image : null,
    callSignText: callSign && typeof callSign.config.text === 'string' ? callSign.config.text : null,
    bannerImage: banner && typeof banner.config.image === 'string' ? banner.config.image : null,
  };
}

/**
 * Shared markup for a player's own identity block — banner behind an
 * emblem + call-sign plate. One implementation reused by the account page,
 * the friends page and the floating social overlay, so "my call sign is
 * missing on page X" can't happen again by one of them simply never having
 * been given the rendering code the others have.
 */
export function playerTagHtml(username: string, tag: PlayerTag, escapeHtml: (s: string) => string): string {
  return `
    <div class="player-tag"${tag.bannerImage ? ` style="background-image:url('${tag.bannerImage}')"` : ''}>
      <div class="player-tag__emblem" style="${tag.emblemImage ? `background-image:url('${tag.emblemImage}');` : ''}"></div>
      <div class="player-tag__id">
        <strong>${escapeHtml(username)}</strong>
        <div class="player-tag__callsign" style="${tag.plateImage ? `background-image:url('${tag.plateImage}');` : ''}">
          <span>${tag.callSignText ? escapeHtml(tag.callSignText) : ''}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * Applies the equipped `timer_face` item's raster art (if any) as a themed
 * backdrop behind the timer, via the `--timer-face-image` custom property
 * `styles/game.css` reads on `.round-mount`. Additive alongside the legacy
 * `signature_colour` accent/background colour theming in `cosmetics.ts` —
 * a player can have both a colour theme and a raster timer-face skin
 * equipped at once, since they're different catalog slots. Most `timer_face`
 * items have no art yet (`config.image` is only set once real Cosmetic
 * Factory output exists for that item — see docs/VISUAL_AXES.md in the
 * Cosmetic Factory tool), so this silently no-ops until then rather than
 * showing a broken image.
 */
export async function applyCachedTimerFaceArt(el: HTMLElement): Promise<void> {
  try {
    const [catalog, inventory] = await Promise.all([fetchCatalog(), fetchInventory()]);
    const equippedId = inventory.equipped['timer_face'];
    const item = equippedId ? catalog.items.find((i) => i.id === equippedId) : undefined;
    const image = item && typeof item.config.image === 'string' ? item.config.image : null;
    el.style.setProperty('--timer-face-image', image ? `url(${JSON.stringify(image)})` : 'none');
  } catch {
    // not signed in or offline; leave whatever backdrop (or none) was already set
  }
}

/** Buys a directly-purchasable item with soft currency (Ticks). Premium (Stripe) items are not sold here. */
export async function purchaseItem(itemId: string): Promise<{ balance: number }> {
  return authorizedFetch<{ ok: true; itemId: string; spent: number; balance: number }>('/api/store/purchase', {
    method: 'POST',
    body: JSON.stringify({ itemId }),
  });
}

export interface CurrencyPack {
  id: string;
  currency: string;
  currencyName: string;
  amount: number;
  priceCents: number;
  name: string;
}

/** The real-money price list for both currencies' packs. This is the only way to buy Tocks (see docs). No auth required. */
export function fetchCurrencyPacks(): Promise<{ packs: CurrencyPack[] }> {
  return authorizedFetch<{ packs: CurrencyPack[] }>('/api/store/packs');
}
